import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import throttle from '../utilities/throttle';

gsap.registerPlugin(Draggable);
gsap.registerPlugin(InertiaPlugin);
gsap.registerPlugin(ScrollToPlugin);

const setup = () => {
  const sliders = document.querySelectorAll('[data-w-slider]');

  let mediaManager = gsap.matchMedia();
  const breakpoint = 640;

  mediaManager.add(
    {
      isDesktop: `(min-width: ${breakpoint}px)`,
      isMobile: `(max-width: ${breakpoint - 1}px)`,
    },
    (context) => {
      let { isDesktop, isMobile } = context.conditions;

      let rotation = isDesktop ? 6 : 4;

      sliders.forEach((container) => {
        const sliderType = container.getAttribute('data-w-slider');
        if (!sliderType) return;

        if (sliderType === 'columns' && isMobile) rotation = 6;

        const prevButton = container.querySelector(
          '[data-w-slider-control="prev"]'
        );
        const nextButton = container.querySelector(
          '[data-w-slider-control="next"]'
        );

        if (sliderType === 'wipe' || (sliderType === 'columns' && isMobile)) {
          const slider = container.querySelector('[data-w-slider-inner]');
          const slides = slider.querySelectorAll('[data-w-slider-item]');

          const updateButtonStatus = (progress) => {
            if (progress === 0) {
              prevButton.classList.add('w-is-disabled');
            } else if (progress === 1) {
              nextButton.classList.add('w-is-disabled');
            } else {
              prevButton.classList.remove('w-is-disabled');
              nextButton.classList.remove('w-is-disabled');
            }
          };
          // const slideDelay = 6;
          const slideDuration = 0.5;
          const numSlides = slides.length;
          const progressPerItem = 1 / (numSlides - 1);
          const snapProgress = gsap.utils.snap(progressPerItem);
          const snapProgressDirectional = (value, direction) => {
            let snapped = snapProgress(value);
            return snapped - value < 0 === direction < 0
              ? snapped
              : snapProgress(
                  direction < 0
                    ? value - progressPerItem
                    : value + progressPerItem
                );
          };
          // const timer = gsap.delayedCall(slideDelay, autoPlay);
          let slideAnimation; // as reference
          let slideWidth;
          let totalWidth;

          gsap.set(slides, {
            // xPercent: (i) => i * 100, // moving sideways
            rotate: (i) => i * rotation,
            '--_opacity': (i) => (i === 0 ? 1 : 0),
            transformOrigin: () => (isDesktop ? '50% 800%' : '50% 1200%'),
          });

          const animation = gsap.timeline({
            paused: true,
          });
          animation.to(
            slides,
            {
              // xPercent: '-=' + (numSlides - 1) * 100, // moving sideways
              rotate: '-=' + (numSlides - 1) * rotation,
              duration: 1,
              ease: 'none',
            },
            0
          );

          const space = progressPerItem * 0.5;
          const firstId = 0;
          const lastId = numSlides - 1;
          slides.forEach((slide, i) => {
            const before = progressPerItem * i - space;
            const after = progressPerItem * i + space;

            if (i !== firstId) {
              animation.to(
                slide,
                // {
                //   opacity: 0,
                // },
                {
                  '--_opacity': 1,
                  duration: space,
                },
                before
              );
            }
            if (i !== lastId) {
              animation.to(
                slide,
                // {
                //   opacity: 1,
                // },
                {
                  '--_opacity': 0,
                  duration: space,
                },
                after
              );
            }
          });

          const tracker = InertiaPlugin.track(animation, 'progress')[0]; // tracks velocity of progress
          const proxy = document.createElement('div'); // use a proxy element
          const draggable = new Draggable(proxy, {
            type: 'x',
            trigger: slider,
            allowContextMenu: true,
            onPress() {
              gsap.killTweensOf(animation);
              this.startProgress = animation.progress();
            },
            onDrag() {
              let change = (draggable.startX - draggable.x) / totalWidth;
              const progress = draggable.startProgress + change;
              animation.progress(progress);
              updateButtonStatus(snapProgress(progress));
            },
            onRelease() {
              let velocity = tracker.get('progress');

              gsap.to(animation, {
                inertia: {
                  duration: { min: slideDuration, max: slideDuration * 2 },
                  progress: {
                    velocity: velocity,
                    min: 0,
                    max: 1,
                    end: (value) => snapProgressDirectional(value, velocity),
                  },
                },
                onUpdate: throttle(() => {
                  const progress = snapProgress(animation.progress());
                  updateButtonStatus(progress);
                }, 250),
                onComplete: function () {
                  const progress = snapProgress(animation.progress());
                  updateButtonStatus(progress);
                },
                overwrite: true,
              });
            },
          });

          const animateSlides = (direction) => {
            let progress = snapProgress(
              animation.progress() + direction * progressPerItem
            );
            if (progress >= 0 && progress <= 1) {
              updateButtonStatus(progress);

              slideAnimation = gsap.to(animation, {
                progress: progress,
                duration: slideDuration,
                overwrite: true,
                // onComplete: () => timer.restart(true),
                onComplete: function () {
                  const progress = snapProgress(animation.progress());
                  updateButtonStatus(progress);
                },
                // onComplete: () => {
                //   slides.forEach((slide) => slide.classList.remove('w-is-active'));
                //   const current = Math.floor(animation.progress() / progressPerItem);
                //   slides[current].classList.add('w-is-active');
                // },
              });
            }
          };

          // function autoPlay() {
          //   if (draggable.isPressed || gsap.isTweening(animation)) {
          //     timer.restart(true);
          //   } else {
          //     animateSlides(1);
          //   }
          // }

          function resize() {
            slideWidth = slides[0].offsetWidth;
            totalWidth = slideWidth * numSlides;
          }
          resize();
          window.addEventListener('resize', throttle(resize, 100));

          prevButton.addEventListener('click', () => {
            animateSlides(-1);
          });
          nextButton.addEventListener('click', () => {
            animateSlides(1);
          });

          updateButtonStatus(0); // init button status
        }
        // else if (isMobile) {
        //   const slider = container.querySelector('[data-w-slider-inner]');
        //   const slides = slider.querySelectorAll('[data-w-slider-item]');

        //   let currentSlide = 0;

        //   prevButton.addEventListener('click', () => {
        //     console.log(currentSlide);

        //     if (slides[currentSlide - 1])
        //       gsap.to(slider, {
        //         duration: 1,
        //         scrollTo: {
        //           y: slides[currentSlide - 1],
        //           // offsetY: (window.innerHeight / 100) * 40, // 40lvh
        //           autoKill: true,
        //         },
        //         ease: 'power2',
        //       });
        //   });
        //   nextButton.addEventListener('click', () => {
        //     console.log(currentSlide);

        //     if (slides[currentSlide + 1])
        //       gsap.to(slider, {
        //         duration: 1,
        //         scrollTo: {
        //           y: slides[currentSlide + 1],
        //           // offsetY: (window.innerHeight / 100) * 40, // 40lvh
        //           autoKill: true,
        //         },
        //         ease: 'power2',
        //       });
        //   });

        //   slides.forEach((slide, index) => {
        //     // const content = slide.querySelector('[data-w-slider-item-content]');

        //     // if (index > 0) {
        //     //   gsap.set(content, {
        //     //     opacity: 0,
        //     //   });
        //     // }

        //     gsap.timeline({
        //       scrollTrigger: {
        //         scroller: slider,
        //         trigger: slide,
        //         start: 'left 50%',
        //         end: 'right 50%',
        //         horizontal: true,
        //         onEnter: () => {
        //           if (index === slides.length - 1)
        //             nextButton.classList.add('w-is-disabled');
        //           else {
        //             nextButton.classList.remove('w-is-disabled');
        //             prevButton.classList.remove('w-is-disabled');
        //           }
        //           currentSlide = index;
        //         },
        //         onEnterBack: () => {
        //           if (index === 0) prevButton.classList.add('w-is-disabled');
        //           else {
        //             nextButton.classList.remove('w-is-disabled');
        //             prevButton.classList.remove('w-is-disabled');
        //           }

        //           currentSlide = index;
        //         },
        //       },
        //     });

        //     prevButton.classList.add('w-is-disabled');

        //     // timeline.to(
        //     //   content,
        //     //   {
        //     //     opacity: 1,
        //     //     duration: 2,
        //     //   },
        //     //   0
        //     // );
        //     // timeline.to(
        //     //   content,
        //     //   {
        //     //     opacity: 0,
        //     //     duration: 2,
        //     //   },
        //     //   4
        //     // );
        //   });
        // }
      });
    }
  );
};

export default function Slider() {
  return {
    init: () => {
      setup();
    },
  };
}
